// screens/staff/StudentDetailScreen.tsx
//
// W6 v1 Session 2 — per-student deep-dive page for the instructor surface.
//
// Three tabs (per spec §5.3):
//   1. Progresso — mastery rings × argomento, current Indice, blind sim history,
//      30-day study rhythm.
//   2. Brain — auto-generated tutor notes + instructor notes (read-only in v1).
//   3. Attività — session timeline + simulations drilldown + content gaps.
//
// Tab 4 (Azioni) is v1.1 — not built.
// URL contract: ?tab=progresso|brain|attivita drives the active tab so deep-
// linking + back-button work.
//
// Italian copy is formal "Lei" voice. Instructors only — segreteria 403s on
// every tab's underlying endpoint.

import { useEffect, useRef, useState, useCallback, type JSX } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTokens } from '../../state/theme';
import { MasteryRing } from '../../components/MasteryRing';
import { formatItalianExamDate } from '../../utils/exam-date';
import {
  staffApi,
  type StudentProgressoPayload,
  type StudentBrainPayload,
  type StudentAttivitaPayload,
  type StudentAttivitaSessionPayload,
  type IndiceTrendPayload,
} from '../../api/staff';

type Tab = 'progresso' | 'brain' | 'attivita' | 'azioni';

const TAB_LABELS: Record<Tab, string> = {
  progresso: 'Progresso',
  brain:     'Brain',
  attivita:  'Attività',
  azioni:    'Azioni',
};

function isTab(s: string | null | undefined): s is Tab {
  return s === 'progresso' || s === 'brain' || s === 'attivita' || s === 'azioni';
}

export default function StudentDetailScreen(): JSX.Element {
  const { studentId } = useParams<{ studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tokens = useTokens();

  const tabFromUrl = searchParams.get('tab');
  const activeTab: Tab = isTab(tabFromUrl) ? tabFromUrl : 'progresso';

  const setActiveTab = useCallback((t: Tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!studentId) {
    return <SectionMsg tone="error" tokens={tokens}>Studente non trovato.</SectionMsg>;
  }

  return (
    <section data-testid="student-detail-screen">
      <header style={{ marginBottom: 16 }}>
        <Link
          to="/staff/studenti"
          data-testid="student-detail-back"
          style={{ color: tokens.textMuted, textDecoration: 'none', fontSize: 13 }}
        >
          ← Torna agli studenti
        </Link>
      </header>

      {/*
        Full WAI-ARIA tabs pattern:
          - role="tablist" on the nav
          - role="tab" + id="student-tab-{key}" + aria-controls="student-panel-{key}"
            on each tab button, with aria-selected on the active tab
          - tabIndex 0 on the active tab, -1 on inactive — Tab key only hits
            the active tab; Arrow keys cycle within the tablist; Home/End
            jump to first/last
          - role="tabpanel" + id="student-panel-{key}" + aria-labelledby="student-tab-{key}"
            on the panel container
        See https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
      */}
      <nav
        role="tablist"
        aria-label="Sezioni dettaglio studente"
        data-testid="student-detail-tabs"
        style={{
          display:      'flex',
          gap:          4,
          borderBottom: `1px solid ${tokens.line}`,
          marginBottom: 24,
        }}
      >
        {(['progresso', 'brain', 'attivita', 'azioni'] as Tab[]).map((t) => {
          const isActive = activeTab === t;
          return (
            <button
              key={t}
              id={`student-tab-${t}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`student-panel-${t}`}
              tabIndex={isActive ? 0 : -1}
              data-testid={`student-tab-${t}`}
              onClick={() => setActiveTab(t)}
              onKeyDown={(e) => {
                const order: Tab[] = ['progresso', 'brain', 'attivita', 'azioni'];
                const idx = order.indexOf(activeTab);
                if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  const next = order[(idx + 1) % order.length];
                  setActiveTab(next);
                  document.getElementById(`student-tab-${next}`)?.focus();
                }
                if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  const prev = order[(idx + order.length - 1) % order.length];
                  setActiveTab(prev);
                  document.getElementById(`student-tab-${prev}`)?.focus();
                }
                if (e.key === 'Home') {
                  e.preventDefault();
                  const first = order[0];
                  setActiveTab(first);
                  document.getElementById(`student-tab-${first}`)?.focus();
                }
                if (e.key === 'End') {
                  e.preventDefault();
                  const last = order[order.length - 1];
                  setActiveTab(last);
                  document.getElementById(`student-tab-${last}`)?.focus();
                }
              }}
              style={{
                background:    'transparent',
                border:        'none',
                borderBottom:  isActive ? `2px solid ${tokens.accent}` : '2px solid transparent',
                padding:       '10px 18px',
                cursor:        'pointer',
                color:         isActive ? tokens.text : tokens.textMuted,
                fontSize:      14,
                fontWeight:    isActive ? 600 : 500,
                fontFamily:    tokens.fonts.body,
              }}
            >
              {TAB_LABELS[t]}
            </button>
          );
        })}
      </nav>

      <div
        id={`student-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`student-tab-${activeTab}`}
        tabIndex={0}
        data-testid={`student-panel-${activeTab}`}
      >
        {activeTab === 'progresso' && <ProgressoPanel studentId={studentId} />}
        {activeTab === 'brain'     && <BrainPanel     studentId={studentId} />}
        {activeTab === 'attivita'  && <AttivitaPanel  studentId={studentId} />}
        {activeTab === 'azioni'    && <AzioniPanel    studentId={studentId} />}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Progresso
// ---------------------------------------------------------------------------

function ProgressoPanel({ studentId }: { studentId: string }): JSX.Element {
  const tokens = useTokens();
  const [data, setData] = useState<StudentProgressoPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    staffApi.studentProgresso(studentId)
      .then((d) => { if (!alive) return; setData(d); setLoading(false); })
      .catch(() => { if (!alive) return; setError('Errore: riprovi tra poco.'); setLoading(false); });
    return () => { alive = false; };
  }, [studentId]);

  if (loading) return <SectionMsg tone="muted" tokens={tokens}>Caricamento…</SectionMsg>;
  if (error)   return <SectionMsg tone="error"  tokens={tokens}>{error}</SectionMsg>;
  if (!data)   return <SectionMsg tone="muted"  tokens={tokens}>Nessun dato disponibile.</SectionMsg>;

  const observedPrinciples = data.perArgomentoMastery.reduce(
    (sum, argomento) => sum + argomento.principles_observed,
    0,
  );
  const hasIndiceEvidence = observedPrinciples > 0;
  const formattedExamDate = formatItalianExamDate(data.student.exam_date);

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: tokens.fonts.serif, fontSize: 22, fontWeight: 700, margin: 0 }}>
          {data.student.first_name}
        </h2>
        <div style={{ color: tokens.textMuted, fontSize: 13, marginTop: 4 }}>
          {data.student.cohort_name ? `Classe ${data.student.cohort_name}` : 'Nessuna classe'}
          {formattedExamDate && ` · Esame ${formattedExamDate}`}
        </div>
      </header>

      {/* Indice di preparazione attuale + 30-day trend (W6 v1.1 Session 3 item 12) */}
      <section data-testid="progresso-indice" style={{ marginBottom: 24, padding: '16px 20px', background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.lg }}>
        <div style={{ fontSize: 12, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
          Indice di preparazione attuale
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontFamily: tokens.fonts.serif, fontSize: 32, fontWeight: 700 }}>
            {data.currentIndice.indice}
          </span>
          <span style={{ color: tokens.textMuted, fontSize: 14 }}>/100</span>
          <span style={{ color: tokens.textMuted, fontSize: 13, marginLeft: 12 }}>
            {hasIndiceEvidence
              ? `(${data.currentIndice.correctCount}/${data.currentIndice.totalPrinciples} principi padroneggiati)`
              : 'Nessuna evidenza ancora'}
          </span>
        </div>
        {hasIndiceEvidence ? (
          <div style={{ fontSize: 12, color: tokens.textFaint, marginTop: 6, lineHeight: 1.35 }}>
            Quanti principi del programma lo studente ha dimostrato di padroneggiare rispondendo; la copertura per argomento è nella griglia sotto.
          </div>
        ) : (
          <div style={{ fontSize: 12, color: tokens.textFaint, marginTop: 6, lineHeight: 1.35 }}>
            I dati appariranno quando lo studente risponderà negli esercizi o nelle simulazioni; le lezioni lo preparano.
          </div>
        )}
        <IndiceTrendChart studentId={data.student.id} />
      </section>

      {/* Mastery rings × argomento (25 entries) */}
      <section data-testid="progresso-rings" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 13, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>
          Padronanza per argomento
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
          {data.perArgomentoMastery.map(arg => (
            <MasteryRing
              key={arg.argomento_id}
              argomentoId={arg.argomento_id}
              nameIt={arg.name_it}
              averageMastery={arg.average_mastery}
              principlesTotal={arg.principles_total}
              principlesObserved={arg.principles_observed}
              principlesMastered={arg.principles_mastered}
              readinessLabel={arg.evidenceLabel}
            />
          ))}
        </div>
      </section>

      {/* Blind simulation history */}
      <section data-testid="progresso-sim-history" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 13, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>
          Storico simulazioni cieche
        </h3>
        {data.blindSimulationHistory.length === 0 ? (
          <SectionMsg tone="muted" tokens={tokens}>Nessuna simulazione cieca completata.</SectionMsg>
        ) : (
          <div role="table" aria-label="Storico simulazioni" style={{ border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, overflow: 'hidden' }}>
            <div role="row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '10px 14px', background: tokens.surface, fontSize: 12, color: tokens.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              <span>Data</span>
              <span>Punteggio</span>
              <span>Esito</span>
            </div>
            {data.blindSimulationHistory.map(sim => (
              <div key={sim.session_id} role="row" data-testid={`sim-row-${sim.session_id}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '12px 14px', borderTop: `1px solid ${tokens.line}`, fontSize: 14 }}>
                <span>{new Date(sim.completed_at).toLocaleDateString('it-IT')}</span>
                <span>{sim.correct}/{sim.total}</span>
                <span style={{ color: sim.passed ? tokens.right : tokens.wrong, fontWeight: 600 }}>
                  {sim.passed ? 'Superata' : 'Non superata'}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Study rhythm — 30-day count */}
      <section data-testid="progresso-rhythm">
        <h3 style={{ fontSize: 13, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>
          Ritmo di studio (ultimi 30gg)
        </h3>
        <div style={{ padding: '16px 20px', background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.lg }}>
          <div style={{ fontSize: 14 }}>
            <strong>{data.sessionDays30.answered_questions_30d}</strong> domande risposte in <strong>{data.sessionDays30.distinct_days_30d}</strong> giorni distinti.
          </div>
        </div>
      </section>
    </div>
  );
}

// 3-tier mastery label + RingCard moved to `components/MasteryRing.tsx` in
// W5 Session 1 — shared with the student-side `<IndicePreparazioneCard />`
// + `MioProgressoScreen` to avoid fork drift. Same color thresholds, same
// 3-tier text label, same WCAG 1.4.1 aria-label format.

// ---------------------------------------------------------------------------
// W6 v1.1 Session 3 (item 12) — 30-day Indice trend chart
// ---------------------------------------------------------------------------
//
// Inline-SVG line/area chart. NO chart library — matches the inline-SVG
// pattern the Session 1 class-pulse heatmap established (Vite bundle
// hygiene; founder direction in the coordinator brief).
//
// Reads from `GET /api/staff/students/:id/indice-trend?days=30`. Empty +
// loading + error states match the rest of Tab 1 Progresso. Italian "Lei"
// labels per spec § + the W6 v1 instructor surface convention.

function IndiceTrendChart({ studentId }: { studentId: string }): JSX.Element {
  const tokens = useTokens();
  const [payload, setPayload] = useState<IndiceTrendPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    staffApi.studentIndiceTrend(studentId, { days: 30 })
      .then((p) => { if (!alive) return; setPayload(p); setLoading(false); })
      .catch(() => { if (!alive) return; setError('Caricamento andamento non riuscito.'); setLoading(false); });
    return () => { alive = false; };
  }, [studentId]);

  if (loading) {
    return (
      <div data-testid="indice-trend-loading" style={{ marginTop: 14, fontSize: 12, color: tokens.textMuted }}>
        Caricamento andamento…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="indice-trend-error" role="alert" style={{ marginTop: 14, fontSize: 12, color: tokens.wrong }}>
        {error}
      </div>
    );
  }
  if (!payload || payload.trend.length === 0) {
    return (
      <div data-testid="indice-trend-empty" style={{ marginTop: 14, fontSize: 12, color: tokens.textMuted, fontStyle: 'italic' }}>
        Non ci sono ancora dati per disegnare l'andamento. La curva apparirà quando lo studente avrà risposto a qualche domanda.
      </div>
    );
  }

  // Layout. Width is set by the container; we use viewBox + preserveAspect
  // so the chart scales without redrawing. 30 days × 320 px viewBox feels
  // right at desktop and tablet widths.
  const VB_W = 320;
  const VB_H = 90;
  const PAD_LEFT   = 28;  // y-axis labels live here
  const PAD_RIGHT  = 6;
  const PAD_TOP    = 6;
  const PAD_BOTTOM = 16;  // x-axis labels (start + end date) live here
  const plotW = VB_W - PAD_LEFT - PAD_RIGHT;
  const plotH = VB_H - PAD_TOP  - PAD_BOTTOM;

  // Dense the points along the X axis. With sparse data (e.g. 5 of 30 days)
  // the points still spread over the full window — students see a "gappy"
  // chart rather than a dense one if they haven't been studying. We use the
  // actual day-since-first-event as the X coord, normalized to [0, 1].
  const points = payload.trend;
  const firstDayIso = points[0]!.date;
  const lastDayIso  = points[points.length - 1]!.date;
  const firstDayMs = new Date(firstDayIso + 'T00:00:00Z').getTime();
  const lastDayMs  = new Date(lastDayIso  + 'T00:00:00Z').getTime();
  const spanMs = Math.max(1, lastDayMs - firstDayMs);

  const xy = points.map(p => {
    const t = new Date(p.date + 'T00:00:00Z').getTime();
    const xNorm = (t - firstDayMs) / spanMs;
    const x = PAD_LEFT + xNorm * plotW;
    const y = PAD_TOP + (1 - p.indice / 100) * plotH;  // top = 100, bottom = 0
    return { x, y, ...p };
  });

  const linePath = xy.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  // Area path: line + close to baseline
  const areaPath = xy.length > 1
    ? `${linePath} L ${xy[xy.length - 1]!.x.toFixed(1)} ${(PAD_TOP + plotH).toFixed(1)} L ${xy[0]!.x.toFixed(1)} ${(PAD_TOP + plotH).toFixed(1)} Z`
    : '';

  // Aria label — describe the trend in one sentence so screen readers get
  // the gist without parsing SVG paths.
  const firstIndice = points[0]!.indice;
  const lastIndice  = points[points.length - 1]!.indice;
  const direction = lastIndice > firstIndice ? 'in salita' : lastIndice < firstIndice ? 'in discesa' : 'stabile';
  const ariaLabel = `Indice negli ultimi ${payload.period_days} giorni: tendenza ${direction} da ${firstIndice} a ${lastIndice}.`;

  // Date formatters — Italian locale.
  const fmtDate = (iso: string) => new Date(iso + 'T00:00:00Z').toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });

  return (
    <div data-testid="indice-trend-chart" style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
        Andamento Indice (ultimi {payload.period_days}gg)
      </div>
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: 110, display: 'block' }}
      >
        {/* Y-axis gridlines + labels at 0 / 50 / 100 */}
        {[0, 50, 100].map((tick) => {
          const y = PAD_TOP + (1 - tick / 100) * plotH;
          return (
            <g key={tick}>
              <line
                x1={PAD_LEFT}
                x2={PAD_LEFT + plotW}
                y1={y}
                y2={y}
                stroke={tokens.line}
                strokeDasharray={tick === 0 ? '0' : '2,3'}
                strokeWidth={0.5}
              />
              <text
                x={PAD_LEFT - 4}
                y={y + 3}
                fontSize={8}
                fontFamily={tokens.fonts.body}
                fill={tokens.textMuted}
                textAnchor="end"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {/* Filled area under the line */}
        {areaPath && (
          <path
            d={areaPath}
            fill={tokens.accentSoft}
            opacity={0.5}
          />
        )}

        {/* The line itself */}
        <path
          d={linePath}
          fill="none"
          stroke={tokens.accent}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Per-point dots with declarative Italian tooltip */}
        {xy.map((p) => (
          <g key={p.date}>
            <circle
              cx={p.x}
              cy={p.y}
              r={2.2}
              fill={tokens.accent}
              data-testid={`indice-trend-point-${p.date}`}
            >
              <title>{`Indice ${p.indice}/100 il ${fmtDate(p.date)}`}</title>
            </circle>
          </g>
        ))}

        {/* X-axis date labels — first + last day only, to avoid clutter */}
        <text
          x={PAD_LEFT}
          y={VB_H - 4}
          fontSize={8}
          fontFamily={tokens.fonts.body}
          fill={tokens.textMuted}
          textAnchor="start"
        >
          {fmtDate(firstDayIso)}
        </text>
        <text
          x={PAD_LEFT + plotW}
          y={VB_H - 4}
          fontSize={8}
          fontFamily={tokens.fonts.body}
          fill={tokens.textMuted}
          textAnchor="end"
        >
          {fmtDate(lastDayIso)}
        </text>
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Brain
// ---------------------------------------------------------------------------

function BrainPanel({ studentId }: { studentId: string }): JSX.Element {
  const tokens = useTokens();
  const [data, setData] = useState<StudentBrainPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  // Compose state for the inline contribute editor + the supersede flow.
  const [composeText, setComposeText] = useState('');
  const [supersedesId, setSupersedesId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    staffApi.studentBrain(studentId)
      .then((d) => { if (!alive) return; setData(d); setLoading(false); })
      .catch(() => { if (!alive) return; setError('Errore: riprovi tra poco.'); setLoading(false); });
    return () => { alive = false; };
  }, [studentId, reloadCounter]);

  const reload = useCallback(() => setReloadCounter(c => c + 1), []);

  async function handleSave() {
    setComposeError(null);
    if (!composeText.trim()) {
      setComposeError('Scriva un\'osservazione prima di salvare.');
      return;
    }
    setSubmitting(true);
    try {
      await staffApi.addBrainNote(studentId, {
        markdown: composeText.trim(),
        supersedes_id: supersedesId ?? undefined,
      });
      setComposeText('');
      setSupersedesId(null);
      setToast(supersedesId ? 'Versione aggiornata.' : 'Osservazione aggiunta.');
      reload();
      window.setTimeout(() => setToast(null), 3500);
    } catch {
      setComposeError('Salvataggio non riuscito. Riprovi.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchive(noteId: string) {
    if (!window.confirm('Archiviare questa osservazione? Resterà nella cronologia ma non verrà più letta dal tutor.')) return;
    try {
      await staffApi.deleteBrainNote(studentId, noteId);
      setToast('Osservazione archiviata.');
      reload();
      window.setTimeout(() => setToast(null), 3500);
    } catch {
      setToast('Archiviazione non riuscita. Riprovi.');
      window.setTimeout(() => setToast(null), 3500);
    }
  }

  function handleStartSupersede(note: StudentBrainPayload['notes'][number]) {
    setSupersedesId(note.id);
    setComposeText(note.observations);
    // Scroll the editor into view for ergonomics — defensive against jsdom.
    document.getElementById('brain-compose-editor')?.focus();
  }

  function handleCancelSupersede() {
    setSupersedesId(null);
    setComposeText('');
  }

  if (loading) return <SectionMsg tone="muted" tokens={tokens}>Caricamento…</SectionMsg>;
  if (error)   return <SectionMsg tone="error" tokens={tokens}>{error}</SectionMsg>;
  if (!data)   return <SectionMsg tone="muted" tokens={tokens}>Nessun dato disponibile.</SectionMsg>;

  // Group: tutor (auto) vs instructor vs system. Order each block by recency.
  const tutorNotes      = data.notes.filter(n => n.actor_kind === 'tutor');
  const instructorNotes = data.notes.filter(n => n.actor_kind === 'instructor');
  const otherNotes      = data.notes.filter(n => n.actor_kind === 'system' || n.actor_kind === 'unknown');

  return (
    <div>
      {/* W6 v1.1 Session 2 — inline "Aggiungi osservazione pedagogica" tool */}
      <section
        data-testid="brain-compose"
        aria-labelledby="brain-compose-title"
        style={{ marginBottom: 28, padding: '16px 18px', background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.lg }}
      >
        <h3 id="brain-compose-title" style={{ fontSize: 14, fontWeight: 600, margin: '0 0 6px 0' }}>
          {supersedesId ? 'Sostituisci osservazione' : 'Aggiungi osservazione pedagogica'}
        </h3>
        <p style={{ fontSize: 12, color: tokens.textMuted, margin: '0 0 10px 0' }}>
          {supersedesId
            ? 'Sta sostituendo una versione precedente. La cronologia resta visibile.'
            : 'Il tutor leggerà la sua osservazione all\'inizio di ogni sessione successiva.'}
        </p>
        <textarea
          id="brain-compose-editor"
          data-testid="brain-compose-editor"
          value={composeText}
          onChange={(e) => setComposeText(e.target.value)}
          rows={5}
          placeholder={"Es. “Lo studente confonde regolarmente le precedenze a destra agli incroci con strisce pedonali. Ho ripreso l'argomento in lezione il 15/04.”"}
          disabled={submitting}
          style={{
            width: '100%',
            background: tokens.bg,
            color: tokens.text,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '10px 12px',
            fontFamily: tokens.fonts.body,
            fontSize: 14,
            resize: 'vertical',
          }}
        />
        {composeError && (
          <div role="alert" style={{ color: tokens.wrong, fontSize: 13, marginTop: 8 }}>{composeError}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          {supersedesId && (
            <button
              type="button"
              data-testid="brain-compose-cancel-supersede"
              onClick={handleCancelSupersede}
              disabled={submitting}
              style={{
                background: 'transparent',
                color: tokens.textMuted,
                border: `1px solid ${tokens.line}`,
                borderRadius: tokens.radius.pill,
                padding: '8px 14px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: 13,
              }}
            >
              Annulla
            </button>
          )}
          <button
            type="button"
            data-testid="brain-compose-save"
            onClick={handleSave}
            disabled={submitting || !composeText.trim()}
            style={{
              background: submitting || !composeText.trim() ? tokens.surface : tokens.accent,
              color: tokens.mode === 'dark' ? '#1a1510' : '#fff',
              border: 'none',
              borderRadius: tokens.radius.pill,
              padding: '8px 16px',
              cursor: submitting || !composeText.trim() ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {submitting ? 'Salvataggio…' : (supersedesId ? 'Sostituisci' : 'Salva osservazione')}
          </button>
        </div>
      </section>

      {toast && (
        <div
          data-testid="brain-compose-toast"
          role="status"
          aria-live="polite"
          style={{
            marginBottom: 16,
            padding: '8px 14px',
            background: tokens.accentSoft,
            color: tokens.accentInk,
            border: `1px solid ${tokens.accent}`,
            borderRadius: tokens.radius.md,
            fontSize: 13,
          }}
        >
          {toast}
        </div>
      )}

      {data.notes.length === 0 ? (
        <div data-testid="brain-empty">
          <SectionMsg tone="muted" tokens={tokens}>Ancora nessuna osservazione: la prima la aggiunga lei qui sopra.</SectionMsg>
        </div>
      ) : (
        <>
          <BrainGroup
            testid="brain-tutor"
            title="Osservazioni del tutor (Il Prof)"
            subtitle="Generate automaticamente alla fine di ogni sessione."
            notes={tutorNotes}
            tokens={tokens}
          />
          <BrainGroup
            testid="brain-instructor"
            title="Osservazioni degli istruttori"
            subtitle="Modificabili dagli istruttori della scuola."
            notes={instructorNotes}
            tokens={tokens}
            writable
            onSupersede={handleStartSupersede}
            onArchive={handleArchive}
          />
          {otherNotes.length > 0 && (
            <BrainGroup
              testid="brain-other"
              title="Altre annotazioni"
              subtitle=""
              notes={otherNotes}
              tokens={tokens}
            />
          )}
        </>
      )}
    </div>
  );
}

function BrainGroup({
  testid,
  title,
  subtitle,
  notes,
  tokens,
  writable,
  onSupersede,
  onArchive,
}: {
  testid:       string;
  title:        string;
  subtitle:     string;
  notes:        StudentBrainPayload['notes'];
  tokens:       ReturnType<typeof useTokens>;
  writable?:    boolean;
  onSupersede?: (note: StudentBrainPayload['notes'][number]) => void;
  onArchive?:   (noteId: string) => void;
}): JSX.Element {
  if (notes.length === 0) {
    return (
      <section data-testid={`${testid}-empty`} style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px 0' }}>{title}</h3>
        {subtitle && <div style={{ fontSize: 12, color: tokens.textMuted, marginBottom: 8 }}>{subtitle}</div>}
        <SectionMsg tone="muted" tokens={tokens}>Nessuna osservazione.</SectionMsg>
      </section>
    );
  }
  return (
    <section data-testid={testid} style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px 0' }}>{title}</h3>
      {subtitle && <div style={{ fontSize: 12, color: tokens.textMuted, marginBottom: 12 }}>{subtitle}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {notes.map(note => (
          <article
            key={note.id}
            data-testid={`brain-note-${note.id}`}
            style={{
              padding:      '14px 18px',
              background:   tokens.surface,
              border:       `1px solid ${tokens.line}`,
              borderRadius: tokens.radius.md,
            }}
          >
            <header style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12, color: tokens.textMuted }}>
              <span>{note.author}</span>
              <span>{new Date(note.created_at).toLocaleString('it-IT')}</span>
            </header>
            <pre style={{ margin: 0, fontFamily: tokens.fonts.body, fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {note.observations}
            </pre>
            {note.prior_version_id && (
              <div style={{ marginTop: 8, fontSize: 12, color: tokens.textMuted }}>
                Versione precedente: <code>{note.prior_version_id.slice(0, 8)}…</code>
              </div>
            )}
            {writable && (onSupersede || onArchive) && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                {onSupersede && (
                  <button
                    type="button"
                    data-testid={`brain-note-supersede-${note.id}`}
                    onClick={() => onSupersede(note)}
                    style={{ background: 'transparent', color: tokens.textMuted, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.pill, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
                  >
                    Sostituisci con nuova versione
                  </button>
                )}
                {onArchive && (
                  <button
                    type="button"
                    data-testid={`brain-note-archive-${note.id}`}
                    onClick={() => onArchive(note.id)}
                    style={{ background: 'transparent', color: tokens.textMuted, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.pill, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
                  >
                    Archivia
                  </button>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Attività
// ---------------------------------------------------------------------------

function AttivitaPanel({ studentId }: { studentId: string }): JSX.Element {
  const tokens = useTokens();
  const [data, setData] = useState<StudentAttivitaPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Record<string, StudentAttivitaSessionPayload | 'loading' | 'error'>>({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    staffApi.studentAttivita(studentId, { page, pageSize: 30 })
      .then((d) => { if (!alive) return; setData(d); setLoading(false); })
      .catch(() => { if (!alive) return; setError('Errore: riprovi tra poco.'); setLoading(false); });
    return () => { alive = false; };
  }, [studentId, page]);

  async function toggleExpand(sessionId: string) {
    setExpanded(prev => {
      if (prev[sessionId]) {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      }
      return { ...prev, [sessionId]: 'loading' };
    });
    try {
      const detail = await staffApi.studentAttivitaSession(studentId, sessionId);
      setExpanded(prev => ({ ...prev, [sessionId]: detail }));
    } catch {
      setExpanded(prev => ({ ...prev, [sessionId]: 'error' }));
    }
  }

  if (loading) return <SectionMsg tone="muted" tokens={tokens}>Caricamento…</SectionMsg>;
  if (error)   return <SectionMsg tone="error" tokens={tokens}>{error}</SectionMsg>;
  if (!data)   return <SectionMsg tone="muted" tokens={tokens}>Nessun dato disponibile.</SectionMsg>;

  return (
    <div>
      {/* Sessions timeline */}
      <section data-testid="attivita-sessions" style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 13, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>
          Sessioni recenti
        </h3>
        {data.sessions.length === 0 ? (
          <SectionMsg tone="muted" tokens={tokens}>Nessuna sessione registrata.</SectionMsg>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.sessions.map(s => (
              <div key={s.session_id} data-testid={`session-row-${s.session_id}`} style={{ background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{prettySessionMode(s.mode)}</div>
                    <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>
                      {new Date(s.started_at).toLocaleString('it-IT')}
                      {s.duration_minutes !== null && ` · ${s.duration_minutes} min`}
                      {s.answered_count > 0 && ` · ${s.correct_count}/${s.answered_count} corrette`}
                    </div>
                  </div>
                  {s.mode.startsWith('simula_') && (
                    <button
                      data-testid={`session-expand-${s.session_id}`}
                      onClick={() => toggleExpand(s.session_id)}
                      style={{ background: 'transparent', color: tokens.textMuted, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.sm, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}
                    >
                      {expanded[s.session_id] ? 'Nascondi' : 'Espandi'}
                    </button>
                  )}
                </div>
                {expanded[s.session_id] === 'loading' && (
                  <div style={{ marginTop: 8, fontSize: 13, color: tokens.textMuted }}>Caricamento dettagli…</div>
                )}
                {expanded[s.session_id] === 'error' && (
                  <div style={{ marginTop: 8, fontSize: 13, color: tokens.wrong }}>Errore nel caricamento.</div>
                )}
                {expanded[s.session_id] && expanded[s.session_id] !== 'loading' && expanded[s.session_id] !== 'error' && (
                  <SessionExpand detail={expanded[s.session_id] as StudentAttivitaSessionPayload} tokens={tokens} />
                )}
              </div>
            ))}
          </div>
        )}
        {data.pagination.has_more && (
          <button
            data-testid="attivita-load-more"
            onClick={() => setPage(p => p + 1)}
            style={{ marginTop: 12, background: 'transparent', color: tokens.textMuted, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.pill, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
          >
            Carica altri
          </button>
        )}
      </section>

      {/* Lezione sessions (Fast / Deep) */}
      <section data-testid="attivita-lezione" style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 13, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>
          Lezioni (Fast / Deep)
        </h3>
        {data.lezioneSessions.length === 0 ? (
          <SectionMsg tone="muted" tokens={tokens}>Nessuna lezione registrata.</SectionMsg>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.lezioneSessions.map(lz => (
              <div key={lz.session_id} data-testid={`lezione-row-${lz.session_id}`} style={{ background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, padding: '12px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {lz.mode === 'fast' ? 'Lezione Fast' : 'Lezione Deep'} · {lz.argomento_name ?? lz.argomento_id}
                </div>
                <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>
                  {new Date(lz.started_at).toLocaleString('it-IT')}
                  {' · '}
                  Stato: {lz.status}
                  {lz.mode === 'deep' && ` · ${lz.turns} turni`}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Content gaps */}
      <section data-testid="attivita-gaps">
        <h3 style={{ fontSize: 13, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>
          Lacune dai Chiedi
        </h3>
        {data.contentGaps.length === 0 ? (
          <SectionMsg tone="muted" tokens={tokens}>Nessuna lacuna registrata da Chiedi.</SectionMsg>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.contentGaps.map(g => (
              <div key={g.id} data-testid={`gap-row-${g.id}`} style={{ background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, padding: '12px 16px' }}>
                <div style={{ fontSize: 14 }}>{g.description}</div>
                <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>
                  {new Date(g.created_at).toLocaleString('it-IT')} · Stato: {g.status}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SessionExpand({
  detail,
  tokens,
}: {
  detail: StudentAttivitaSessionPayload;
  tokens: ReturnType<typeof useTokens>;
}): JSX.Element {
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${tokens.line}` }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
        {detail.events.map(ev => {
          const correct = ev.student_answer === ev.correct_answer;
          return (
            <div key={ev.sequence_number} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 24, textAlign: 'right', color: tokens.textMuted }}>{ev.sequence_number}.</span>
              <span style={{ color: correct ? tokens.right : tokens.wrong, fontWeight: 700, width: 16 }}>
                {correct ? '✓' : '✗'}
              </span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ev.question_text ?? '—'}
              </span>
              <span style={{ color: tokens.textMuted, fontSize: 12 }}>
                Sua: {fmtVF(ev.student_answer)} · Corr: {fmtVF(ev.correct_answer)}
                {ev.response_time_ms !== null && ` · ${(ev.response_time_ms / 1000).toFixed(1)}s`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function fmtVF(b: boolean | null): string {
  if (b === true)  return 'V';
  if (b === false) return 'F';
  return '—';
}

function prettySessionMode(mode: string): string {
  switch (mode) {
    case 'allena':           return 'Allena';
    case 'simula_cieca':     return 'Simulazione cieca';
    case 'simula_guidata':   return 'Simulazione guidata';
    case 'simula_difficile': return 'Simulazione difficile';
    case 'chiedi':           return 'Chiedi al Prof';
    default: return mode;
  }
}

// ---------------------------------------------------------------------------
// Tab 4 — Azioni (W6 v1.1 Session 2 — spec §5.3.4)
// ---------------------------------------------------------------------------

function AzioniPanel({ studentId }: { studentId: string }): JSX.Element {
  const tokens = useTokens();

  // Pull the student row (cohort + paused state) via the existing Progresso
  // endpoint — it already returns student + exam_date and we layer the
  // pause-state lookup on top. We re-call studentProgresso on action success
  // to refresh the panel; pause/unpause UPDATEs come back inline.
  const [progresso, setProgresso] = useState<StudentProgressoPayload | null>(null);
  const [studentMeta, setStudentMeta] = useState<{
    paused_at:                   string | null;
    paused_by_school_staff_id:   string | null;
    paused_reason:               string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  // Action UI state
  const [messageDraft, setMessageDraft] = useState('');
  const [messageBusy, setMessageBusy] = useState(false);
  const [flagNote, setFlagNote] = useState('');
  const [flagBusy, setFlagBusy] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseBusy, setPauseBusy] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // W6 v1.1 review fix — modal a11y: store trigger so we can restore focus on
  // close. Mirrors the LacuneQueueScreen ModalShell pattern (Esc + focus
  // capture + restoration). Closes the W6 v1 Frequenze modal regression
  // instead of reproducing it on a fresh modal.
  const pauseTriggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    staffApi.studentProgresso(studentId)
      .then((d) => {
        if (!alive) return;
        setProgresso(d);
        // Pull pause metadata via a follow-up to listStudents
        // (the progresso payload doesn't include paused_at). We use the
        // roster list as a poor man's lookup; segreteria-only fields are
        // already filtered.
        return staffApi.listStudents();
      })
      .then((roster) => {
        if (!alive || !roster) return;
        const me = roster.students.find((s) => s.id === studentId);
        if (me) {
          // RosterStudent only has `paused`. The new pause fields aren't on
          // that endpoint yet. We surface paused-state from the boolean and
          // leave reason/by null — the unpause flow doesn't need them.
          setStudentMeta({
            paused_at:                 me.paused ? 'paused' : null,
            paused_by_school_staff_id: null,
            paused_reason:             null,
          });
        }
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError('Errore: riprovi tra poco.');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [studentId, reloadCounter]);

  const reload = useCallback(() => setReloadCounter(c => c + 1), []);

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  }

  async function handleSendMessage() {
    if (!messageDraft.trim()) return;
    setMessageBusy(true);
    try {
      await staffApi.sendStudentMessage(studentId, messageDraft.trim());
      setMessageDraft('');
      showToast('Messaggio inviato. Lo studente lo troverà nella sua casella.');
    } catch {
      showToast('Invio non riuscito. Riprovi tra poco.');
    } finally {
      setMessageBusy(false);
    }
  }

  async function handleFlag() {
    setFlagBusy(true);
    try {
      await staffApi.flagOneOnOne(studentId, flagNote.trim() || undefined);
      setFlagNote('');
      showToast('Studente segnalato per un 1-a-1.');
    } catch {
      showToast('Segnalazione non riuscita. Riprovi.');
    } finally {
      setFlagBusy(false);
    }
  }

  async function handlePauseConfirm() {
    setPauseBusy(true);
    try {
      const r = await staffApi.pauseStudent(studentId, pauseReason.trim() || undefined);
      setStudentMeta({
        paused_at:                 r.student.paused_at,
        paused_by_school_staff_id: r.student.paused_by_school_staff_id,
        paused_reason:             r.student.paused_reason,
      });
      setPauseReason('');
      setShowPauseConfirm(false);
      showToast('Account in pausa. Lo studente non potrà accedere finché non lo riattiva.');
      reload();
    } catch {
      showToast('Pausa non riuscita. Riprovi.');
    } finally {
      setPauseBusy(false);
    }
  }

  async function handleUnpause() {
    setPauseBusy(true);
    try {
      await staffApi.unpauseStudent(studentId);
      setStudentMeta({ paused_at: null, paused_by_school_staff_id: null, paused_reason: null });
      showToast('Account riattivato. Lo studente può accedere di nuovo.');
      reload();
    } catch {
      showToast('Riattivazione non riuscita. Riprovi.');
    } finally {
      setPauseBusy(false);
    }
  }

  if (loading) return <SectionMsg tone="muted" tokens={tokens}>Caricamento…</SectionMsg>;
  if (error)   return <SectionMsg tone="error" tokens={tokens}>{error}</SectionMsg>;
  if (!progresso) return <SectionMsg tone="muted" tokens={tokens}>Nessun dato disponibile.</SectionMsg>;

  const isPaused = studentMeta?.paused_at !== null && studentMeta?.paused_at !== undefined;

  return (
    <div data-testid="azioni-panel">
      <header style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: tokens.fonts.serif, fontSize: 22, fontWeight: 700, margin: 0 }}>
          Azioni su {progresso.student.first_name}
        </h2>
        <div style={{ color: tokens.textMuted, fontSize: 13, marginTop: 4 }}>
          Strumenti per intervenire fuori dalle sessioni: messaggio asincrono, segnalazione interna, pausa account.
        </div>
      </header>

      {toast && (
        <div
          data-testid="azioni-toast"
          role="status"
          aria-live="polite"
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            background: tokens.accentSoft,
            color: tokens.accentInk,
            border: `1px solid ${tokens.accent}`,
            borderRadius: tokens.radius.md,
            fontSize: 13,
          }}
        >
          {toast}
        </div>
      )}

      {/* Section A — Async message to student */}
      <section
        data-testid="azioni-message"
        style={{ marginBottom: 28, padding: '16px 18px', background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.lg }}
      >
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 6px 0' }}>Invia messaggio allo studente</h3>
        <p style={{ fontSize: 12, color: tokens.textMuted, margin: '0 0 10px 0' }}>
          Lo studente vedrà il suo messaggio nella casella «Messaggi» della propria app. Comunicazione a senso unico.
        </p>
        <textarea
          data-testid="azioni-message-textarea"
          value={messageDraft}
          onChange={(e) => setMessageDraft(e.target.value)}
          rows={5}
          placeholder="Es. “Ricordi di rivedere l'argomento Precedenze prima della prossima lezione.”"
          disabled={messageBusy}
          style={{
            width: '100%',
            background: tokens.bg,
            color: tokens.text,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '10px 12px',
            fontFamily: tokens.fonts.body,
            fontSize: 14,
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button
            type="button"
            data-testid="azioni-message-send"
            onClick={handleSendMessage}
            disabled={messageBusy || !messageDraft.trim()}
            style={{
              background: messageBusy || !messageDraft.trim() ? tokens.surface : tokens.accent,
              color: tokens.mode === 'dark' ? '#1a1510' : '#fff',
              border: 'none',
              borderRadius: tokens.radius.pill,
              padding: '8px 16px',
              cursor: messageBusy || !messageDraft.trim() ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {messageBusy ? 'Invio…' : 'Invia messaggio'}
          </button>
        </div>
      </section>

      {/* Section B — Flag for 1-on-1 (internal-only) */}
      <section
        data-testid="azioni-flag"
        style={{ marginBottom: 28, padding: '16px 18px', background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.lg }}
      >
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 6px 0' }}>Segnala per 1-a-1</h3>
        <div
          data-testid="azioni-flag-banner"
          style={{
            margin: '0 0 10px 0',
            padding: '8px 12px',
            background: tokens.bg,
            border: `1px dashed ${tokens.line}`,
            borderRadius: tokens.radius.md,
            fontSize: 12,
            color: tokens.textMuted,
          }}
        >
          Visibile solo agli istruttori della scuola; lo studente non riceve notifica.
        </div>
        <textarea
          data-testid="azioni-flag-textarea"
          value={flagNote}
          onChange={(e) => setFlagNote(e.target.value)}
          rows={3}
          placeholder="Nota interna (facoltativa)"
          disabled={flagBusy}
          style={{
            width: '100%',
            background: tokens.bg,
            color: tokens.text,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '10px 12px',
            fontFamily: tokens.fonts.body,
            fontSize: 14,
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button
            type="button"
            data-testid="azioni-flag-submit"
            onClick={handleFlag}
            disabled={flagBusy}
            style={{
              background: flagBusy ? tokens.surface : tokens.accent,
              color: tokens.mode === 'dark' ? '#1a1510' : '#fff',
              border: 'none',
              borderRadius: tokens.radius.pill,
              padding: '8px 16px',
              cursor: flagBusy ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {flagBusy ? 'Segnalazione…' : 'Segnala per un 1-a-1'}
          </button>
        </div>
      </section>

      {/* Section C — Pause account */}
      <section
        data-testid="azioni-pause"
        style={{ marginBottom: 28, padding: '16px 18px', background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.lg }}
      >
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 6px 0' }}>Pausa account</h3>
        {isPaused ? (
          <div data-testid="azioni-pause-active">
            <div
              style={{
                margin: '0 0 12px 0',
                padding: '10px 12px',
                background: tokens.bg,
                border: `1px solid ${tokens.wrong}`,
                borderRadius: tokens.radius.md,
                fontSize: 13,
                color: tokens.text,
              }}
            >
              <strong>Account attualmente in pausa.</strong>
              {studentMeta?.paused_reason && (
                <div style={{ marginTop: 6, color: tokens.textMuted, fontSize: 12 }}>
                  Motivo: {studentMeta.paused_reason}
                </div>
              )}
              <div style={{ marginTop: 6, color: tokens.textMuted, fontSize: 12 }}>
                Lo studente vede una schermata «Account in pausa» fino alla riattivazione.
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                data-testid="azioni-pause-unpause"
                onClick={handleUnpause}
                disabled={pauseBusy}
                style={{
                  background: pauseBusy ? tokens.surface : tokens.accent,
                  color: tokens.mode === 'dark' ? '#1a1510' : '#fff',
                  border: 'none',
                  borderRadius: tokens.radius.pill,
                  padding: '8px 16px',
                  cursor: pauseBusy ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {pauseBusy ? 'Riattivazione…' : 'Riattiva account'}
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="azioni-pause-inactive">
            <p style={{ fontSize: 13, color: tokens.textMuted, margin: '0 0 12px 0' }}>
              Mette in pausa l'account dello studente. Le sessioni in corso vengono interrotte; lo studente non potrà accedere finché non lo riattiva. Reversibile in qualsiasi momento.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                ref={pauseTriggerRef}
                type="button"
                data-testid="azioni-pause-open"
                onClick={() => setShowPauseConfirm(true)}
                disabled={pauseBusy}
                style={{
                  background: 'transparent',
                  color: tokens.textMuted,
                  border: `1px solid ${tokens.wrong}`,
                  borderRadius: tokens.radius.pill,
                  padding: '8px 16px',
                  cursor: pauseBusy ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Pausa account
              </button>
            </div>

            {showPauseConfirm && (
              <PauseConfirmModal
                studentFirstName={progresso.student.first_name}
                pauseReason={pauseReason}
                pauseBusy={pauseBusy}
                onChangeReason={setPauseReason}
                onConfirm={handlePauseConfirm}
                onCancel={() => { setShowPauseConfirm(false); setPauseReason(''); }}
                returnFocusTo={pauseTriggerRef}
              />
            )}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function SectionMsg({
  tone,
  children,
  tokens,
}: {
  tone:     'muted' | 'error';
  children: React.ReactNode;
  tokens:   ReturnType<typeof useTokens>;
}): JSX.Element {
  return (
    <div role={tone === 'error' ? 'alert' : undefined} style={{ color: tone === 'error' ? tokens.wrong : tokens.textMuted, fontSize: 14, padding: '20px 0' }}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pause-confirmation modal — W6 v1.1 review fix #4
// ---------------------------------------------------------------------------
//
// Mirrors the LacuneQueueScreen ModalShell pattern (Esc dismissal, focus
// capture on mount, focus restoration on close, backdrop-click closes). The
// secondary "Annulla" button is the initial focus target so an accidental
// Enter doesn't trigger the destructive confirm. Closes the W6 v1 Frequenze
// modal regression instead of repeating it on a fresh modal.

function PauseConfirmModal({
  studentFirstName,
  pauseReason,
  pauseBusy,
  onChangeReason,
  onConfirm,
  onCancel,
  returnFocusTo,
}: {
  studentFirstName: string;
  pauseReason:      string;
  pauseBusy:        boolean;
  onChangeReason:   (s: string) => void;
  onConfirm:        () => void;
  onCancel:         () => void;
  returnFocusTo:    React.RefObject<HTMLElement | null>;
}): JSX.Element {
  const tokens = useTokens();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Focus the secondary action on mount; restore focus to the trigger on close.
  // Esc handler dismisses the modal. (No Tab focus-trap — same scope as the
  // LacuneQueueScreen ModalShell; full trap is a v1.2 a11y polish item.)
  useEffect(() => {
    cancelRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !pauseBusy) {
        e.preventDefault();
        onCancel();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      // Only restore focus if the trigger element is still in the DOM.
      const node = returnFocusTo.current;
      if (node && document.contains(node)) {
        node.focus();
      }
    };
  }, [onCancel, pauseBusy, returnFocusTo]);

  return (
    <div
      data-testid="azioni-pause-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="azioni-pause-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget && !pauseBusy) onCancel(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: tokens.surface,
          border: `1px solid ${tokens.line}`,
          borderRadius: tokens.radius.lg,
          padding: '20px 22px',
          maxWidth: 440,
          width: '100%',
        }}
      >
        <h4 id="azioni-pause-modal-title" style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px 0' }}>
          Confermi la pausa?
        </h4>
        <p style={{ fontSize: 13, color: tokens.text, margin: '0 0 12px 0' }}>
          Mettere in pausa l'account di <strong>{studentFirstName}</strong>? Lo studente non potrà accedere finché non lo riattiva.
        </p>
        <label style={{ fontSize: 12, color: tokens.textMuted, display: 'block', marginBottom: 4 }}>
          Motivo (facoltativo, visibile solo agli istruttori)
        </label>
        <textarea
          data-testid="azioni-pause-reason"
          value={pauseReason}
          onChange={(e) => onChangeReason(e.target.value)}
          rows={3}
          disabled={pauseBusy}
          style={{
            width: '100%',
            background: tokens.bg,
            color: tokens.text,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '8px 10px',
            fontFamily: tokens.fonts.body,
            fontSize: 13,
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button
            ref={cancelRef}
            type="button"
            data-testid="azioni-pause-cancel"
            onClick={onCancel}
            disabled={pauseBusy}
            style={{
              background: 'transparent',
              color: tokens.textMuted,
              border: `1px solid ${tokens.line}`,
              borderRadius: tokens.radius.pill,
              padding: '8px 14px',
              cursor: pauseBusy ? 'not-allowed' : 'pointer',
              fontSize: 13,
            }}
          >
            Annulla
          </button>
          <button
            type="button"
            data-testid="azioni-pause-confirm"
            onClick={onConfirm}
            disabled={pauseBusy}
            style={{
              background: pauseBusy ? tokens.surface : tokens.wrong,
              color: '#fff',
              border: 'none',
              borderRadius: tokens.radius.pill,
              padding: '8px 16px',
              cursor: pauseBusy ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {pauseBusy ? 'Pausa…' : 'Conferma pausa'}
          </button>
        </div>
      </div>
    </div>
  );
}
